import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, ShieldCheck, ArrowUpRight } from "lucide-react";
import { COMPLIANCES } from "../data/content";
import { useModals } from "./modals/ModalProvider";

export default function ComplianceBadges() {
  const { openComplianceDetail } = useModals();

  return (
    <section id="compliance" className="py-20 sm:py-28 relative overflow-hidden">
      {/* Colorful backdrop */}
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <div className="absolute top-10 left-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-10 right-1/4 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary">Compliance frameworks</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Certified paths to <span className="text-gradient-brand">audit readiness.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Click any framework to see the scope, mandate and exactly what Techmate delivers to get you audit-ready.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {COMPLIANCES.map((c, i) => (
            <motion.button
              key={c.code}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              onClick={() => openComplianceDetail(c)}
              data-testid={`compliance-badge-${c.code.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="card-lift group text-left rounded-3xl border border-border bg-card p-5 sm:p-6 relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {/* decorative corner */}
              <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-primary via-primary/40 to-transparent opacity-10 group-hover:opacity-25 transition-opacity" />
              <div className="relative flex items-start justify-between">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary to-accent text-white grid place-items-center shrink-0 shadow-lg shadow-primary/20">
                  <ShieldCheck className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 text-secondary px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest">
                  <BadgeCheck className="h-3 w-3" /> {c.scope?.split("·")[0]?.trim() || "Global"}
                </span>
              </div>
              <div className="relative mt-4">
                <div className="font-display text-2xl font-black tracking-tight">{c.code}</div>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {c.subtitle}
                </div>
              </div>
              <p className="relative mt-3 text-xs sm:text-[13px] text-muted-foreground leading-relaxed line-clamp-2">
                {c.desc}
              </p>
              <div className="relative mt-4 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  {c.scope?.split("·")[1]?.trim() || "Certifiable"}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary group-hover:gap-1.5 transition-all">
                  Explore <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
